<template>
  <div class="flex flex-col gap-4">
    <h2 class="text-xl font-semibold">{{ t('title') }}</h2>

    <Input v-model="search" :placeholder="t('search')" variant="ghost" />

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner class="text-hazel-600" />
    </div>

    <template v-else>
      <div :class="table({ class: 'grid-cols-[repeat(6,minmax(max-content,1fr))]' })">
        <div :class="tableHeader()">
          <div :class="tableCell()">{{ t('booking') }}</div>
          <div :class="tableCell()">{{ t('date') }}</div>
          <div :class="tableCell()">{{ t('typeAndCounterparty') }}</div>
          <div :class="tableCell()">{{ t('invoices') }}</div>
          <div :class="tableCell()">{{ t('reference') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('amount') }}</div>
        </div>
        <div v-for="item in payments" :key="item.id" :class="tableRow()">
          <div :class="tableCell()">
            <router-link
              v-if="item.booking?.id"
              :to="{ name: 'bookingEdit', params: { id: item.booking.id } }"
              class="text-link"
            >
              {{
                item.booking.bookingNumber
                  ? t('bookingLabel', { number: item.booking.bookingNumber })
                  : t('openBooking')
              }}
            </router-link>
            <span v-else>{{ t('noBooking') }}</span>
          </div>
          <div :class="tableCell()">
            {{ formatPaidDate(item) }}
          </div>
          <div :class="tableCell()">
            <div class="flex flex-wrap items-center gap-2">
              <span>{{ item.paymentType ? t(`paymentTypes.${item.paymentType}`) : '-' }}</span>
              <CounterpartyTypeBadge
                v-if="item.counterpartyType"
                :counterparty-type="item.counterpartyType"
                size="sm"
              />
            </div>
          </div>
          <div :class="tableCell()">
            <div v-if="item.allocatedInvoices?.length" class="flex flex-col gap-0.5">
              <router-link
                v-for="invoice in item.allocatedInvoices"
                :key="invoice.id"
                :to="{ name: 'invoice', params: { id: invoice.id } }"
                class="text-link"
              >
                {{ invoice.completeInvoiceNumber || t('draftInvoice') }}
              </router-link>
            </div>
            <span v-else>-</span>
          </div>
          <div :class="tableCell()">
            <span class="font-mono text-xs">{{ item.referenceId || '-' }}</span>
          </div>
          <div :class="tableCell({ numeric: true })">
            {{ formatCurrency(item.amount) }}
          </div>
        </div>
      </div>

      <EntityListExceptions
        :status="status"
        :error="error as any"
        :items-count="payments.length"
        :no-items-text="searchDebounced ? t('noSearchResults') : t('empty')"
        small
      />

      <div v-if="hasNextPage" class="flex justify-center">
        <CVButton outline :is-loading="isFetchingNextPage" @click="() => fetchNextPage()">
          {{ t('loadMore') }}
        </CVButton>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import Input from '@/components/Input.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import CounterpartyTypeBadge from '@/components/badges/CounterpartyTypeBadge.vue';
import { useSettlementUnsettledPayments } from '@/queries/use-settlement';
import { formatCurrency } from '@/utils/format-numbers';
import { formatDate } from '@/hooks/use-formatted-date';
import { table, tableCell, tableHeader, tableRow } from '@carvia/theme/lib/cva/grid';
import { refDebounced } from '@vueuse/core';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Payment } from '@carvia/ros-client-types';

const { t } = useI18n();
const search = ref('');
const searchDebounced = refDebounced(search, 300);
const { legalEntityId } = useLegalEntityFilter();

const { data, isLoading, status, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
  useSettlementUnsettledPayments(searchDebounced, legalEntityId);

const payments = computed(() => data.value?.pages.flatMap((page) => page.items) ?? []);

const formatPaidDate = (item: Payment) => {
  const date = item.paidDate ?? item.date;
  return date ? formatDate(date, DateTime.DATE_SHORT) : '-';
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Unsettled payments",
    "search": "Search booking number or payment reference…",
    "booking": "Booking",
    "date": "Date",
    "typeAndCounterparty": "Type / counterparty",
    "invoices": "Invoices",
    "draftInvoice": "Draft",
    "reference": "Reference",
    "amount": "Amount",
    "bookingLabel": "Booking {number}",
    "openBooking": "Open booking",
    "noBooking": "No booking",
    "empty": "No unsettled payments",
    "noSearchResults": "No payments match the search",
    "loadMore": "Load more"
  },
  "de": {
    "title": "Noch nicht abgerechnete Zahlungen",
    "search": "Buchungsnummer oder Zahlungsreferenz suchen…",
    "booking": "Buchung",
    "date": "Datum",
    "typeAndCounterparty": "Typ / Gegenpartei",
    "invoices": "Rechnungen",
    "draftInvoice": "Entwurf",
    "reference": "Referenz",
    "amount": "Betrag",
    "bookingLabel": "Buchung {number}",
    "openBooking": "Buchung öffnen",
    "noBooking": "Keine Buchung",
    "empty": "Keine noch nicht abgerechneten Zahlungen",
    "noSearchResults": "Keine Zahlungen passen zur Suche",
    "loadMore": "Mehr laden"
  },
  "es": {
    "title": "Pagos pendientes de liquidar",
    "search": "Buscar número de reserva o referencia de pago…",
    "booking": "Reserva",
    "date": "Fecha",
    "typeAndCounterparty": "Tipo / contraparte",
    "invoices": "Facturas",
    "draftInvoice": "Borrador",
    "reference": "Referencia",
    "amount": "Importe",
    "bookingLabel": "Reserva {number}",
    "openBooking": "Abrir reserva",
    "noBooking": "Sin reserva",
    "empty": "No hay pagos pendientes de liquidar",
    "noSearchResults": "Ningún pago coincide con la búsqueda",
    "loadMore": "Cargar más"
  }
}
</i18n>
